import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';

import { formatPrice } from 'common/formatters';
import { INCOME_VERIFICATION_PROVIDER_NAME_MAPPING } from 'common/incomeVerificationConstants';
import {
  SR_APPLICATION_ASSEMBLY_LINE_STATUSES,
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED,
} from 'common/applicationAssemblyLineConstants';
import {
  PROOF_OF_INCOME_PAYTSTUBS,
  PROOF_OF_INCOME_PINWHEEL_PAYTSTUBS,
  PROOF_OF_INCOME_OFFER_LETTER,
  PROOF_OF_INCOME_BANK_STATEMENTS,
  PROOF_OF_INCOME_ADDITIONAL_DOCUMENTS,
  PROOF_OF_INCOME_TYPES,
  PROOF_OF_INCOME_TYPE_TO_LABEL_MAPPING,
} from 'common/householdApplication/incomeHelpers';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrFlex from 'client/components/SrFlex';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_BLUE,
  SR_BUTTON_THEME_ADOBE_WHITE_BLUE_BORDER,
} from 'client/components/SrButton/SrButton';

const BUTTON_TITLE_BY_INCOME_TYPE = {
  [PROOF_OF_INCOME_PAYTSTUBS]: 'View PayStubs',
  [PROOF_OF_INCOME_PINWHEEL_PAYTSTUBS]: 'View PayStubs',
  [PROOF_OF_INCOME_OFFER_LETTER]: 'View Offer Letter',
  [PROOF_OF_INCOME_BANK_STATEMENTS]: 'View Statements',
  [PROOF_OF_INCOME_ADDITIONAL_DOCUMENTS]: 'View Docs',
};
const DOCUMENTS_UPLOADED_TITLE_BY_INCOME_TYPE = {
  [PROOF_OF_INCOME_PAYTSTUBS]: (documents) => `${pluralize('Paystub', documents, true)} uploaded`,
  [PROOF_OF_INCOME_PINWHEEL_PAYTSTUBS]: (documents) => `${pluralize('Paystub', documents, true)} uploaded`,
  [PROOF_OF_INCOME_OFFER_LETTER]: (documents) => `${documents} Offer letter uploaded`,
  [PROOF_OF_INCOME_BANK_STATEMENTS]: (documents) => `${pluralize('Bank Statement', documents, true)} uploaded`, // eslint-disable-line max-len
  [PROOF_OF_INCOME_ADDITIONAL_DOCUMENTS]: (documents) => `${pluralize('Document', documents, true)} uploaded`,
};

const SrIncomeVerificationCardAssemblyLine = ({
  amount,
  onView,
  status,
  incomeType,
  documentsCount,
  voieStartedDate,
  incomeShouldCount,
  incomeVerificationProvider,
}) => {
  let title = PROOF_OF_INCOME_TYPE_TO_LABEL_MAPPING[incomeType];
  const buttonTitle = BUTTON_TITLE_BY_INCOME_TYPE[incomeType];
  const amountFormatted = amount ? formatPrice(amount) : '$-';
  const documentsUploaded = DOCUMENTS_UPLOADED_TITLE_BY_INCOME_TYPE[incomeType](documentsCount);

  let borderColor;
  let amountColor;
  let buttonTheme;
  let cornerText;
  let cornerTextColor;

  if (typeof incomeShouldCount === 'boolean' && !incomeShouldCount) {
    borderColor = '1px solid #D4D6D8';
    amountColor = 'greyMonochromeInactive';
    buttonTheme = SR_BUTTON_THEME_ADOBE_WHITE_BLUE_BORDER;
    cornerText = 'Not used';
    cornerTextColor = 'grey400';
  }
  else if (status === SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED) {
    borderColor = '1px solid #00A238';
    amountColor = 'green';
    buttonTheme = SR_BUTTON_THEME_ADOBE_BLUE;
    cornerText = 'Used in Total Income';
    cornerTextColor = 'green';
  }
  else {
    borderColor = '1px solid #D4D6D8';
    amountColor = 'fontBlueDark';
    buttonTheme = SR_BUTTON_THEME_ADOBE_BLUE;
    cornerText = 'Needs review';
    cornerTextColor = 'orange300';
  }

  const voieStartedDateFormatted = voieStartedDate
    ? moment(voieStartedDate).format('MMM D, YYYY')
    : '';

  const startedDateDaysFromNow = moment().diff(moment(voieStartedDate), 'days');

  if (incomeVerificationProvider) {
    title = INCOME_VERIFICATION_PROVIDER_NAME_MAPPING[incomeVerificationProvider];
  }

  return (
    <SrFlex
      padding='16px'
      direction='col'
      borderRadius='7px'
      border={borderColor}
    >
      <SrBox marginBottom='4px'>
        <SrFlex justify='between'>
          <SrText
            font='greycliff'
            weight='700'
            fontSize='12px'
            lineHeight='18px'
          >
            {title}
          </SrText>
          <SrText
            font='greycliff'
            weight='500'
            fontSize='12px'
            color={cornerTextColor}
            lineHeight='18px'
          >
            {cornerText}
          </SrText>
        </SrFlex>
      </SrBox>
      <SrFlex marginBottom='2px' direction='col'>
        <SrText
          font='greycliff'
          weight='800'
          fontSize='28px'
          lineHeight='34px'
          letterSpacing='1px'
          color={amountColor}
        >
          {amountFormatted}
        </SrText>
      </SrFlex>
      <SrText font='greycliff' weight='500' fontSize='12px' lineHeight='18px'>
        Total Monthly Income
      </SrText>
      {!incomeVerificationProvider && (
        <SrBox marginTop='8px'>
          <SrText font='greycliff' weight='400' fontSize='10px' lineHeight='12px'>
            {documentsUploaded}
          </SrText>
        </SrBox>
      )}
      <SrBox marginTop='2px'>
        <SrText font='greycliff' weight='400' fontSize='10px' lineHeight='12px'>
          {voieStartedDate && (
            <>
              Start date:
              <SrText font='greycliff' weight='700' fontSize='10px' lineHeight='12px'>
                {voieStartedDateFormatted} ({startedDateDaysFromNow}d ago)
              </SrText>
            </>
          )}
        </SrText>
      </SrBox>
      <SrBox marginTop='16px'>
        <SrButton
          theme={buttonTheme}
          onClick={onView}
          padding='0'
        >
          {buttonTitle}
        </SrButton>
      </SrBox>
    </SrFlex>
  );
};

SrIncomeVerificationCardAssemblyLine.propTypes = {
  onView: PropTypes.func.isRequired,
  status: PropTypes.oneOf(SR_APPLICATION_ASSEMBLY_LINE_STATUSES).isRequired,
  incomeType: PropTypes.oneOf(PROOF_OF_INCOME_TYPES).isRequired,
  amount: PropTypes.number.isRequired,
  documentsCount: PropTypes.number.isRequired,
  incomeShouldCount: PropTypes.bool,
  incomeVerificationProvider: PropTypes.string,
  voieStartedDate: PropTypes.string,
};

SrIncomeVerificationCardAssemblyLine.displayName = 'SrIncomeVerificationCardAssemblyLine';

export default SrIncomeVerificationCardAssemblyLine;
